import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineDocument,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../services';
import masterDataService from '../../services/masterDataService';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';

const LossSubTypes = () => {
  const [lossSubTypes, setLossSubTypes] = useState([]);
  const [lossTypes, setLossTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [lossTypeFilter, setLossTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLossSubType, setEditingLossSubType] = useState(null);
  const [formData, setFormData] = useState({
    lossTypeId: '',
    subTypeName: '',
    description: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadLossSubTypes();
      loadLossTypes();
    } else {
      setError('Please login to view loss sub types');
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId) {
      loadLossSubTypes();
    }
  }, [lossTypeFilter, tenantId]);

  const loadLossSubTypes = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getLossSubTypes(tenantId, lossTypeFilter || null);
      setLossSubTypes(data);
    } catch (err) {
      setError('Failed to load loss sub types. Please try again.');
      console.error('Error loading loss sub types:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLossTypes = async () => {
    try {
      const data = await masterDataService.getLossTypes(tenantId);
      setLossTypes(data);
    } catch (err) {
      console.error('Error loading loss types:', err);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredLossSubTypes = lossSubTypes.filter((subType) =>
    subType.subTypeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subType.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingLossSubType(null);
    setFormData({
      lossTypeId: '',
      subTypeName: '',
      description: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (lossSubType) => {
    setEditingLossSubType(lossSubType);
    setFormData({
      lossTypeId: lossSubType.lossTypeId ? lossSubType.lossTypeId.toString() : '',
      subTypeName: lossSubType.subTypeName || '',
      description: lossSubType.description || '',
      status: lossSubType.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteLossSubType(id);
      await loadLossSubTypes();
      setDeleteConfirm(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to delete loss sub type. Please try again.';
      setError(errorMessage);
      console.error('Error deleting loss sub type:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    const nameRegex = /^[A-Za-z0-9\s().,/-]+$/;

    if (!formData.lossTypeId) {
      errors.lossTypeId = 'Loss type is required';
    }

    if (!formData.subTypeName.trim()) {
      errors.subTypeName = 'Sub type name is required';
    } else if (formData.subTypeName.length > 100) {
      errors.subTypeName = 'Sub type name must be less than 100 characters';
    } else if (!nameRegex.test(formData.subTypeName.trim())) {
      errors.subTypeName = 'Name contains invalid characters';
    } else {
      // Duplicate check: Same name within the same Loss Type
      const isDuplicate = lossSubTypes.some(
        (st) =>
          st.subTypeName?.toLowerCase() === formData.subTypeName.trim().toLowerCase() &&
          st.lossTypeId?.toString() === formData.lossTypeId?.toString() &&
          st.subTypeId !== editingLossSubType?.subTypeId
      );
      if (isDuplicate) {
        errors.subTypeName = 'This sub type already exists for the selected loss type';
      }
    }

    if (formData.description && formData.description.length > 255) {
      errors.description = 'Description must be less than 255 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const lossSubTypeData = {
        tenantId: tenantId,
        lossTypeId: formData.lossTypeId ? parseInt(formData.lossTypeId) : null,
        subTypeName: formData.subTypeName.trim(),
        description: formData.description.trim() || null,
        status: formData.status,
        lossType: null // Navigation property
      };

      if (editingLossSubType) {
        lossSubTypeData.subTypeId = editingLossSubType.subTypeId;
        await masterDataService.updateLossSubType(editingLossSubType.subTypeId, lossSubTypeData);
      } else {
        await masterDataService.createLossSubType(lossSubTypeData);
      }

      await loadLossSubTypes();
      setShowModal(false);
      setFormData({ lossTypeId: '', subTypeName: '', description: '', status: 'Active' });
      setEditingLossSubType(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save loss sub type. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLossSubType(null);
    setFormData({ lossTypeId: '', subTypeName: '', description: '', status: 'Active' });
    setFormErrors({});
  };

  const getLossTypeName = (lossTypeId) => {
    if (!lossTypeId) return '-';
    const lossType = lossTypes.find(lt => lt.lossTypeId === lossTypeId);
    return lossType ? lossType.lossTypeName : '-';
  };

  if (loading) {
    return <PageLoading message="Loading loss sub types..." />;
  }

  return (
    <div className="loss-sub-types-page">
      <div className="page-header">
        <div className="header-content">
          <h2>Loss Sub Types</h2>
          <p>Manage loss sub types for detailed downtime tracking</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus className="btn-icon" />
          Add Loss Sub Type
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search loss sub types..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <select
              value={lossTypeFilter}
              onChange={(e) => setLossTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Loss Types</option>
              {lossTypes.map((lt) => (
                <option key={lt.lossTypeId} value={lt.lossTypeId}>
                  {lt.lossTypeName}
                </option>
              ))}
            </select>
          </div>
          <div className="table-info">
            {filteredLossSubTypes.length} {filteredLossSubTypes.length === 1 ? 'sub type' : 'sub types'}
          </div>
        </div>

        {filteredLossSubTypes.length === 0 ? (
          <div className="empty-state">
            <HiOutlineDocument className="empty-icon" />
            <h3>No loss sub types found</h3>
            <p>
              {searchTerm || lossTypeFilter
                ? 'Try adjusting your search or filter terms'
                : 'Get started by creating your first loss sub type'}
            </p>
            {!searchTerm && !lossTypeFilter && (
              <button className="btn-primary" onClick={handleAdd}>
                New Loss Sub Type
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Loss Type</th>
                <th>Sub Type Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLossSubTypes.map((subType, index) => (
                <tr key={subType.subTypeId}>
                  <td>{index + 1}</td>
                  <td>{getLossTypeName(subType.lossTypeId)}</td>
                  <td className="name-cell">{subType.subTypeName}</td>
                  <td className="description-cell">{subType.description || '-'}</td>
                  <td>
                    <span className={`status-badge ${subType.status?.toLowerCase()}`}>
                      {subType.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {subType.createdAt
                      ? new Date(subType.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(subType)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(subType)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingLossSubType ? 'Edit Loss Sub Type' : 'New Loss Sub Type'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={handleCloseModal} disabled={submitting}>Cancel</button>
            <button type="submit" form="loss-subtype-form" className="btn-primary" disabled={submitting}>
              {submitting ? (editingLossSubType ? 'Updating...' : 'Creating...') : (editingLossSubType ? 'Update' : 'Create')}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="loss-subtype-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="lossTypeId">Loss Type</label>
              <select
                id="lossTypeId"
                value={formData.lossTypeId}
                onChange={(e) =>
                  setFormData({ ...formData, lossTypeId: e.target.value })
                }
                className={formErrors.lossTypeId ? 'error' : ''}
              >
                <option value="">Select Loss Type</option>
                {lossTypes.map((lt) => (
                  <option key={lt.lossTypeId} value={lt.lossTypeId}>
                    {lt.lossTypeName}
                  </option>
                ))}
              </select>
              {formErrors.lossTypeId && (
                <span className="field-error">{formErrors.lossTypeId}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="subTypeName">
                Sub Type Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="subTypeName"
                value={formData.subTypeName}
                onChange={(e) =>
                  setFormData({ ...formData, subTypeName: e.target.value })
                }
                className={formErrors.subTypeName ? 'error' : ''}
                placeholder="e.g., Motor Failure, Bearing Wear, Electrical Fault"
                maxLength={100}
              />
              {formErrors.subTypeName && (
                <span className="field-error">{formErrors.subTypeName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter description"
                rows="3"
                maxLength={255}
                className={formErrors.description ? 'error' : ''}
              />
              {formErrors.description && (
                <span className="field-error">{formErrors.description}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.subTypeId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.subTypeName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The loss sub type will be permanently deleted.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default LossSubTypes;

